import { MENU_TIER_LOCK_HREF_TO_KEY, hasFeatureAccess, type AccountType, type AppArea } from "@shared/schema";
import { targetsForNavHref, type ResolvedGate } from "@shared/feature-gates";
import { privateBetaForPath } from "@shared/private-property-betas";
import { isHiddenAppPath } from "@shared/hidden-sections";
import { gateBlockForRoute } from "@/hooks/use-feature-gates";

/** Every Home widget must declare its destination before it can be displayed. */
export const HOME_PANEL_HREFS: Record<string, string> = {
  connections: "/app/connections",
  approvals: "/app/operations/approvals",
  buyBoxReports: "/app/buy-box/reports",
  maintenance: "/app/tasks",
};

const PRIVATE_BETA_DESTINATIONS = [
  { href: "/app/financials/collections", key: "collections" },
  { href: "/app/operations/trash", key: "trash-service" },
  { href: "/app/analytics", key: "portfolio_analytics" },
  { href: "/app/operations/owners", key: "owner_portal" },
  { href: "/app/operations/owner-invoices", key: "owner_portal" },
  { href: "/app/account/pm-billing", key: "owner_portal" },
  { href: "/app/financials/cards", key: "mercury_card_provisioning" },
] as const;

const AREA_DESTINATIONS: Array<{ href: string; area: AppArea }> = [
  { href: "/app/properties", area: "properties" },
  { href: "/app/acquisitions", area: "properties" },
  { href: "/app/renovations", area: "properties" },
  { href: "/app/buy", area: "properties" },
  { href: "/app/sell", area: "properties" },
  { href: "/app/property-launch", area: "properties" },
  { href: "/app/buy-box", area: "properties" },
  { href: "/app/research", area: "properties" },
  { href: "/app/finance", area: "properties" },
  { href: "/app/find-realtor", area: "properties" },
  { href: "/app/underwriting", area: "underwriting" },
  { href: "/app/members", area: "members" },
  { href: "/app/pricing", area: "pricing" },
  { href: "/app/marketing", area: "pricing" },
  { href: "/app/messages", area: "messages" },
  { href: "/app/devices", area: "devices" },
  { href: "/app/financials", area: "financials" },
  { href: "/app/tasks", area: "maintenance" },
  { href: "/app/cleaning", area: "maintenance" },
  { href: "/app/realtor-listings", area: "properties" },
  { href: "/app/operations", area: "maintenance" },
  { href: "/app/supplies", area: "maintenance" },
];

function matches(path: string, prefix: string) {
  return path === prefix || path.startsWith(`${prefix}/`);
}

export interface HomeCardAccess {
  loaded: boolean;
  betaKeys: readonly string[];
  gates: Map<string, ResolvedGate>;
  deniedKeys: readonly string[];
  canView: (area: AppArea) => boolean;
  plan: AccountType;
  modular: boolean;
}

export function isHomeCardVisible(href: string, access: HomeCardAccess): boolean {
  if (!access.loaded) return false;
  const path = href.split(/[?#]/, 1)[0];
  if (["/app/roadmap", "/app/testimonials", "/app/devices/cameras"].some(prefix => matches(path, prefix))) return false;
  // B19 — sections switched off in shared/hidden-sections.ts never get a Home card.
  if (isHiddenAppPath(path)) return false;

  const beta = privateBetaForPath(path) ?? PRIVATE_BETA_DESTINATIONS.find(feature => matches(path, feature.href));
  if (beta && !access.betaKeys.includes(beta.key)) return false;
  if (gateBlockForRoute(access.gates, path)) return false;
  if (targetsForNavHref(path).some(target => target.group && access.gates.get(target.key)?.gated)) return false;

  const lockHref = Object.keys(MENU_TIER_LOCK_HREF_TO_KEY)
    .filter(candidate => !candidate.includes("#") && matches(path, candidate))
    .sort((a, b) => b.length - a.length)[0];
  if (lockHref && access.deniedKeys.includes(MENU_TIER_LOCK_HREF_TO_KEY[lockHref])) return false;

  const area = AREA_DESTINATIONS.find(destination => matches(path, destination.href))?.area;
  if (area && !access.canView(area)) return false;
  if (matches(path, "/app/underwriting") && !access.canView("properties")) return false;
  if (!access.modular && (
    matches(path, "/app/devices") ||
    matches(path, "/app/supplies") ||
    matches(path, "/app/analytics") ||
    matches(path, "/app/marketing")
  ) && !hasFeatureAccess(access.plan, "Professional")) return false;
  return true;
}